import { ApiStatus, UCenterServices } from 'network'
import { CheckUtils, encryptPassword } from 'utils'
import { useAppInfoStore, useBaseInfoStore, useStaticTableDataStore, useUserInfoStore } from 'store'
import { PageRoutes } from '@/constants/PageRoutes'
import { UiUtils, type SerializerPhone } from 'ui-components'
import { useI18n } from 'i18n-utils'
import { GT4 } from 'ui-components'
import { useChangeLanguage } from 'store'
import { SvIds } from 'store/src/constants/static-table-sv-id'
import { storageDB, StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { useForgetPasswordStore } from './use-forget-password'

type LoginFormData = {
  account: string
  area: string
}

export enum VerifyType {
  Google = 'google',
  Email = 'email',
  Mobile = 'mobile',
}

export enum ConfirmPasswordStatus {
  /** 不需要验证，直接去登录 */
  None = 'none',
  /** 密码验证失败，或者表单验证失败 */
  Fail = 'fail',
  /** 需要安全验证 */
  NeedVerify = 'needVerify',
}

export const useLoginStore = defineStore('loginStore', () => {
  const formData = ref<LoginFormData>({
    account: '',
    area: '+86',
  })
  const forgetPasswordStore = useForgetPasswordStore()
  const checkPasswordLoading = ref(false)
  const router = useRouter()
  const appInfoStore = useAppInfoStore()
  const { t } = useI18n()
  const checkPasswordFormData = ref({
    password: '',
  })
  const loginCheckResult = ref<ApiResponse.LoginCheckResult>()
  const tokenLogin = ref<string>('')
  const searchArea = ref<string>('')
  const staticTableStore = useStaticTableDataStore()
  const checkAccountLoading = ref(false)
  const showLoginSecurityAuth = ref(false)
  const tempToken = ref('')
  const baseInfoStore = useBaseInfoStore()
  const route = useRoute()
  const serializerPhone = ref<SerializerPhone>({
    raw: '',
    e164: '',
    country: '',
  })

  const { setToken, refreshUserInfo, setUserInfo } = useUserInfoStore()
  const userInfoStore = useUserInfoStore()

  const checkIsAgree = async () => {
    await refreshUserInfo()
    const val = await storageDB.get(StorageIndexDBEnum.AgreeNotice + userInfoStore.userInfo?.userId)
    if (!val && userInfoStore.isLogin) {
      appInfoStore.showSecurityAgreeDialog = true
    } else {
      appInfoStore.showSecurityAgreeDialog = false
    }
  }
  const handleLoginResult = (res: FetchResponse<ApiResponse.LoginResponse> | undefined) => {
    if (!res) return
    if (res.status === 200) {
      setToken(res.data.tokenInfo.token)
      refreshUserInfo()
      if (route.query.redirect) {
        router.replace(route.query.redirect as string)
      } else {
        router.replace(PageRoutes.Home)
      }
      setTimeout(() => {
        checkIsAgree()
      }, 400)
    }
  }

  const isUsername = computed(() => {
    return !CheckUtils.isValidNumber(formData.value.account) && !formData.value.account.includes('@')
  })

  const isMobile = computed(() => {
    return CheckUtils.isValidNumber(formData.value.account)
  })

  const isEmail = computed(() => {
    return formData.value.account.includes('@')
  })

  const checkAccount = async (geeTestResult?: GeeTestResult) => {
    checkAccountLoading.value = true
    const res = await UCenterServices.loginCheckAccount({
      loginName: formData.value.account,
      ...(geeTestResult || {}),
    })
    checkAccountLoading.value = false
    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return
    }

    loginCheckResult.value = res.data
    tokenLogin.value = res.data.tokenLogin
    forgetPasswordStore.setLoginCheckResult(res.data)
    router.push({
      path: PageRoutes.LoginInputPassword,
      query: {
        redirect: route.query.redirect,
      },
    })
  }

  const loginStep1 = async () => {
    const account = formData.value.account
    if (account.trim().length === 0) {
      UiUtils.showError(t('请输入手机号、邮箱或用户名'))
      return false
    } else if (CheckUtils.isValidNumber(account)) {
      const area = formData.value.area
      if (!area) {
        UiUtils.showError(t('请选择正确的手机区域'))
        return
      }
      if (!CheckUtils.isValidMobile(account)) {
        UiUtils.showError(t('请输入正确的手机号'))
        return false
      }
    } else if (account.includes('@')) {
      if (!CheckUtils.isValidEmail(account)) {
        UiUtils.showError(t('请输入正确的邮箱'))
        return false
      }
    }
    if (appInfoStore.needCaptcha) {
      checkAccountLoading.value = true
      const geeTestResult = await GT4.verify({
        captchaId: baseInfoStore.noRegisterGeeTestConfig?.captchaId,
      })
      if (geeTestResult) {
        await checkAccount(geeTestResult)
      } else {
        checkAccountLoading.value = false
      }
    } else {
      await checkAccount()
    }
  }

  useChangeLanguage(async () => {
    await staticTableStore.fetchStaticTableData([SvIds.StaticSmsCountry])
  })

  const smsCodes = computed(() => {
    const data = staticTableStore.staticTableDataMap[SvIds.StaticSmsCountry]
    if (!data) return []
    return Object.values(data)
  })

  /** 开始登录， 正式登录 */
  const startLogin = async (tokenVerify?: string) => {
    checkPasswordLoading.value = true
    if (tokenVerify) {
      // 存在tokenVerify，代表安全验证通过，需要校验tokenVerify
      const res = await UCenterServices.loginCheckSafetyVerify({
        tokenVerify,
        tokenLogin: tokenLogin.value,
        loginName: formData.value.account,
      })
      if (res.status !== ApiStatus.Success) {
        UiUtils.showError(res.msg)
        return
      }
    }

    const res = await UCenterServices.login({
      loginName: formData.value.account,
      tokenLogin: tokenLogin.value,
    })
    checkPasswordLoading.value = false
    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return
    }
    setUserInfo(undefined)
    handleLoginResult(res)
  }

  /**
   * 确认密码
   * 需要注意的是，onConfirmPassword返回值会交给LoginSecurityAuth组件，用于判断是否需要验证, 如果返回为false, 代表不用验证，否则需要验证
   */
  const onConfirmPassword = async (): Promise<ConfirmPasswordStatus> => {
    if (!checkPasswordFormData.value.password) {
      UiUtils.showError(t('请输入密码'))
      return ConfirmPasswordStatus.Fail
    }
    checkPasswordLoading.value = true
    const res = await UCenterServices.loginCheckPassword({
      loginName: formData.value.account,
      pwd: encryptPassword(checkPasswordFormData.value.password),
      tokenLogin: tokenLogin.value,
    })
    checkPasswordLoading.value = false
    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return ConfirmPasswordStatus.Fail
    }
    loginCheckResult.value = res.data
    if (res.data.status === 3) {
      // 需要验证
      tempToken.value = res.data.token
      setUserInfo(res.data.loginInfo as unknown as ApiResponse.OnlineUserInfo)
      return ConfirmPasswordStatus.NeedVerify
    }
    if (res.data.status === 4) {
      // 不需要验证
      return ConfirmPasswordStatus.None
    }
    return ConfirmPasswordStatus.Fail
  }

  const onOpenLoginSecurityAuth = () => {
    showLoginSecurityAuth.value = true
  }

  const onCloseLoginSecurityAuth = () => {
    showLoginSecurityAuth.value = false
  }

  return {
    formData,
    checkIsAgree,
    loginStep1,
    checkPasswordFormData,
    loginCheckResult,
    tokenLogin,
    isMobile,
    isEmail,
    isUsername,
    searchArea,
    smsCodes,
    checkAccountLoading,
    onConfirmPassword,
    showLoginSecurityAuth,
    onOpenLoginSecurityAuth,
    onCloseLoginSecurityAuth,
    startLogin,
    tempToken,
    checkPasswordLoading,
    serializerPhone,
  }
})
